import { devWarn } from "./dev-logger";

const injectConditionFields = (condition, originalFields) => {
  const index = originalFields.findIndex(
    field =>
      field.name ===
      (condition.insertAfter ? condition.insertAfter : condition.fieldName)
  );
  return index !== -1
    ? [
        ...originalFields.slice(0, index + 1),
        ...condition.fields,
        ...originalFields.slice(index + 1)
      ]
    : originalFields;
};

export const compareEqualConditional = (fieldValue, conditionValue) => {
  const conditionArray = conditionValue?.split("|") || [];
  return conditionArray.some(val => val === fieldValue);
};

export const compareNotEqualConditional = (fieldValue, conditionValue) => {
  const conditionArray = conditionValue?.split("|") || [];
  return conditionArray.every(
    val =>
      val !== (typeof fieldValue === "object" ? fieldValue.value : fieldValue)
  );
};

export const getFieldValue = fieldOrValue => {
  // fieldOrValue might be something like "" that's why returns it instead of null or undefined
  if (!fieldOrValue) {
    return fieldOrValue;
  }
  // eslint-disable-next-line no-prototype-builtins
  if (fieldOrValue.hasOwnProperty("value")) {
    return fieldOrValue.value;
  }
  return fieldOrValue;
};

const renderChainsOfConditionals = ({
  appData,
  newFields,
  conditionals,
  condition
}) => {
  const fieldValue = getFieldValue(appData.values[condition.fieldName]);

  if (condition.type === "equal") {
    // This warning is added because iOS also contains key/value mapping,
    //   but only for equal condition
    //   However, this behavior does not appear to exist in Android
    // Leave as warning until can find a working example
    if (fieldValue && typeof fieldValue !== "string") {
      devWarn(
        "conditional field value not a string",
        condition,
        fieldValue,
        typeof fieldValue
      );
    }

    if (compareEqualConditional(fieldValue, condition.value)) {
      newFields = injectConditionFields(condition, newFields);
    }
  } else if (condition.type === "notEqual") {
    if (compareNotEqualConditional(fieldValue, condition.value)) {
      newFields = injectConditionFields(condition, newFields);
    }
  } else {
    devWarn("Unknown condition type", condition);
  }

  if (condition.fields?.length) {
    condition.fields.forEach(field => {
      const conditionalsToCheck = conditionals.filter(
        conditional => conditional.fieldName === field.name
      );
      conditionalsToCheck.forEach(conditional => {
        newFields = renderChainsOfConditionals({
          appData,
          newFields,
          conditionals,
          condition: conditional
        });
      });
    });
  }
  return newFields;
};

const enableConditionalFields = (screen, appData) => {
  // It appears that currently only fields can have conditionals
  // Only 2 existing condition types are equal or notEqual in iOS
  // There exists a condition updateFieldValue in Android but it's usage is unclear

  /* eslint-disable no-extra-boolean-cast */
  let newFields = [...(screen.fields || [])];
  if (!!screen.conditionals?.length) {
    const fieldsNames = screen.fields.reduce((acc, field) => {
      acc.push(field.name);
      return acc;
    }, []);
    const parentsConditionals = screen.conditionals.filter(conditional =>
      fieldsNames.includes(conditional.fieldName)
    );

    (parentsConditionals || screen.conditionals).forEach(condition => {
      newFields = renderChainsOfConditionals({
        appData,
        newFields,
        conditionals: screen.conditionals,
        condition
      });
    });
  } else {
    (screen.fields || []).forEach(field => {
      const { rules, name, options } = field;
      let selectedOption = { score: 0 };
      const value = appData.values[name];

      if (rules) {
        if (value && options) {
          options.forEach(option => {
            if (value === option.value) {
              selectedOption = option;
              // In case when we go to previous page with already populated data
              // we have to set selected option of field with populated value
              if (!option.selected) {
                option.selected = true;
              }
            }
          });
        }

        const currentAssesment = appData.assesmentsState[name];
        // here we initialize the score and visibility of the assesment which rendered first time
        if (!currentAssesment) {
          appData.assesmentChanged({
            [name]: {
              score: selectedOption.score,
              visible: rules ? !rules.length : true
            }
          });
        }
        // here we set new score of assesment when it is changed
        else if (currentAssesment.score !== selectedOption.score) {
          appData.assesmentChanged({
            [name]: {
              score: selectedOption.score,
              visible: currentAssesment.visible
            }
          });
        }

        if (!!rules.length) {
          // we go inside forEach () only if the field has some rules
          /* eslint-disable array-callback-return */
          rules.forEach(rule => {
            const totalScore = rule.questions.reduce((sum, question) => {
              if (appData.assesmentsState[question]) {
                return sum + appData.assesmentsState[question].score;
              }
            }, 0);

            let isVisible = false;

            switch (rule.comparator) {
              case "EQ":
                isVisible = totalScore === rule.score;
                break;
              case "GT":
                isVisible = totalScore > rule.score;
                break;
              case "GTEQ":
                isVisible = totalScore >= rule.score;
                break;
              default:
            }

            // if visibility of field has changed
            if (currentAssesment && currentAssesment.visible !== isVisible) {
              // if we hide the field we also delete the value which was set
              if (!isVisible) {
                if (options) {
                  options.forEach(option => {
                    option.selected = false;
                  });
                }
                appData.handleChange({
                  [name]: undefined
                });
                appData.resetFieldValidation(name);
              }
              // here we set a new visibility state of assessment
              appData.assesmentChanged({
                [name]: {
                  score: currentAssesment.score,
                  visible: isVisible
                }
              });
            }
          });
        }
      }
    });
  }
  return newFields;
};

export default enableConditionalFields;
